/**
 * Lab Master — Cube Testing + generic Material (aggregate) testing.
 * New for the RMC conversion. Backend: routers/lab.py.
 * Replaces the generic EPC Quality/Safety modules — an RMC plant's quality
 * IS its concrete lab.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, FlaskConical, ClipboardCheck,
  CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { formatDate } from '../lib/format';

const TABS = ['Dashboard', 'Cube Tests', 'Material Tests'];

export default function Lab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Dashboard');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Dashboard' ? {} : { tab: t }); };

  return (
    <div data-testid="lab-page">
      <PageHeader
        overline="Quality"
        title="Lab Master"
        subtitle="Cube compressive-strength testing and IS-code aggregate property tests."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
              data-testid={`lab-tab-${t.toLowerCase().replace(/\s+/g, '-')}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Dashboard' && <DashboardTab />}
          {tab === 'Cube Tests' && <CubeTestsTab />}
          {tab === 'Material Tests' && <MaterialTestsTab />}
        </div>
      </div>
    </div>
  );
}

function KTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}><Icon size={13} /></div>}
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-sm w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{title}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ResultPill({ result }) {
  if (result === 'pass') return <span className="tag bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Pass</span>;
  if (result === 'fail') return <span className="tag bg-red-50 text-red-700 inline-flex items-center gap-1"><XCircle size={11} /> Fail</span>;
  return <span className="tag bg-amber-50 text-amber-700 inline-flex items-center gap-1"><Clock size={11} /> Pending</span>;
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet('/lab/dashboard').then(setData).catch(() => toast.error('Failed to load dashboard')); }, []);
  if (!data) return <div className="text-slate-400 py-8 text-center text-sm">Loading…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KTile label="Cube Tests Due" value={data.cube_tests_due} icon={Clock}
          accent={data.cube_tests_due ? 'text-amber-700 bg-amber-50' : 'text-slate-600 bg-slate-100'} />
        <KTile label="Tested This Month" value={data.tested_this_month} icon={FlaskConical} accent="text-[#1F7A6E] bg-teal-50" />
        <KTile label="Pass Rate This Month" value={data.pass_rate_pct != null ? `${data.pass_rate_pct}%` : '—'}
          icon={CheckCircle2} accent={data.pass_rate_pct >= 95 ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'} />
        <KTile label="Material Tests Logged" value={data.material_test_count} icon={ClipboardCheck} />
      </div>
      {data.failed_this_month > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-sm text-red-700">
          <span className="font-display font-bold">{data.failed_this_month}</span> cube test{data.failed_this_month === 1 ? '' : 's'} failed this month — check the Cube Tests tab.
        </div>
      )}
    </div>
  );
}

// ── Cube Tests ───────────────────────────────────────────────────────────────
function CubeTestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCast, setShowCast] = useState(false);
  const [resultFor, setResultFor] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/lab/cube-tests').then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load cube tests')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Cube Tests · {items.length}</div>
        <button onClick={() => setShowCast(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="cube-test-cast-btn">
          <Plus size={13} /> Cast Sample
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Test No</th><th>Grade</th><th>Batch</th><th>Cast Date</th>
            <th>Age</th><th>Scheduled Test</th><th className="text-right">Strength (MPa)</th><th>Result</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No cube tests yet — cast your first sample above.</td></tr>}
            {items.map((c) => (
              <tr key={c.id}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{c.cube_test_no}</td>
                <td className="font-display font-bold">{c.grade}</td>
                <td className="text-xs">{c.batch_no || '—'}</td>
                <td className="text-xs">{formatDate(c.cast_date)}</td>
                <td className="text-xs">{c.age_days}d</td>
                <td className="text-xs">{formatDate(c.scheduled_test_date)}</td>
                <td className="text-right tabular-nums font-display font-bold">{c.compressive_strength_mpa ?? '—'}</td>
                <td><ResultPill result={c.status === 'tested' ? c.result : null} /></td>
                <td className="text-right">
                  {c.status === 'pending_test' && (
                    <button onClick={() => setResultFor(c)}
                      className="px-3 py-1 text-xs font-semibold text-[#1F7A6E] border border-[#1F7A6E]/40 rounded-sm hover:bg-teal-50"
                      data-testid={`cube-test-result-${c.id}`}>
                      Record Result
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCast && <CastCubeModal onClose={() => setShowCast(false)} onSaved={() => { setShowCast(false); load(); }} />}
      {resultFor && <RecordResultModal test={resultFor} onClose={() => setResultFor(null)} onSaved={() => { setResultFor(null); load(); }} />}
    </div>
  );
}

function CastCubeModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ grade: '', batch_no: '', age_days: '28', sample_id: '', jobsite: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.grade.trim()) return toast.error('Grade is required');
    setSaving(true);
    try {
      const c = await apiPost('/lab/cube-tests', { ...form, age_days: Number(form.age_days) });
      toast.success(`${c.cube_test_no} registered — test due ${new Date(c.scheduled_test_date).toLocaleDateString('en-IN')}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to register sample'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Cast Cube Sample" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Grade"><input value={form.grade} onChange={set('grade')} className="input" placeholder="M25" autoFocus /></Field>
          <Field label="Test Age (days)">
            <select value={form.age_days} onChange={set('age_days')} className="input">
              <option value="7">7 days</option>
              <option value="28">28 days</option>
              <option value="56">56 days</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Batch No"><input value={form.batch_no} onChange={set('batch_no')} className="input" placeholder="Optional" /></Field>
          <Field label="Sample ID"><input value={form.sample_id} onChange={set('sample_id')} className="input" placeholder="1 of 3" /></Field>
        </div>
        <Field label="Jobsite"><input value={form.jobsite} onChange={set('jobsite')} className="input" placeholder="Optional" /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Register Sample'}
        </button>
      </div>
    </ModalShell>
  );
}

function RecordResultModal({ test, onClose, onSaved }) {
  const [loadKn, setLoadKn] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const previewStrength = loadKn ? (Number(loadKn) * 1000 / 22500).toFixed(2) : null;
  const previewResult = previewStrength && test.target_strength_mpa != null
    ? (Number(previewStrength) >= test.target_strength_mpa - 4 ? 'pass' : 'fail') : null;

  const save = async () => {
    if (!loadKn || Number(loadKn) <= 0) return toast.error('Enter the failure load in kN');
    setSaving(true);
    try {
      const r = await apiPatch(`/lab/cube-tests/${test.id}/result`, { load_kn: Number(loadKn), remarks });
      toast[r.result === 'fail' ? 'error' : 'success'](`${test.cube_test_no}: ${r.compressive_strength_mpa} MPa — ${(r.result || 'recorded').toUpperCase()}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record result'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Record Result — ${test.cube_test_no}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
          Grade <span className="font-display font-bold text-slate-800">{test.grade}</span> · Design strength{' '}
          <span className="font-display font-bold text-slate-800">{test.target_strength_mpa ?? '—'} MPa</span> · Standard 150mm cube (22,500 mm²)
        </div>
        <Field label="Failure Load (kN)"><input type="number" value={loadKn} onChange={(e) => setLoadKn(e.target.value)} className="input" autoFocus /></Field>
        {previewStrength && (
          <div className="text-sm flex items-center justify-between bg-white border border-slate-200 rounded-sm p-3">
            <span>Compressive strength: <span className="font-display font-bold">{previewStrength} MPa</span></span>
            {previewResult && <ResultPill result={previewResult} />}
          </div>
        )}
        <Field label="Remarks (optional)"><textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input" rows={2} /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save Result'}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Material Tests ───────────────────────────────────────────────────────────
function MaterialTestsTab() {
  const [items, setItems] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/lab/material-tests').then((r) => { setItems(r.items || []); setTestTypes(r.test_types || []); })
      .catch(() => toast.error('Failed to load material tests')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="kpi-overline">Material Tests · {items.length}</div>
        <button onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2"
          data-testid="material-test-add-btn">
          <Plus size={13} /> Log Test
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Test No</th><th>Type</th><th>Material</th><th>Sample Date</th><th>Results</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-400">No material tests logged yet.</td></tr>}
            {items.map((t) => (
              <tr key={t.id}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{t.test_no}</td>
                <td>{t.test_type}</td>
                <td className="font-semibold">{t.material_name}</td>
                <td className="text-xs">{formatDate(t.sample_date)}</td>
                <td className="text-xs text-slate-600">
                  {Object.entries(t.results || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddMaterialTestModal testTypes={testTypes.length ? testTypes : undefined} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

const DEFAULT_TEST_TYPES = [
  'Sieve Analysis', 'Flakiness & Elongation Index', 'Aggregate Impact Value',
  'Aggregate Crushing Value', 'Specific Gravity & Water Absorption',
  'Silt Content', 'Moisture Content', 'Fly Ash Fineness', 'Bulk Density',
];

function AddMaterialTestModal({ testTypes, onClose, onSaved }) {
  const types = testTypes || DEFAULT_TEST_TYPES;
  const [testType, setTestType] = useState(types[0]);
  const [materialName, setMaterialName] = useState('');
  const [resultRows, setResultRows] = useState([{ key: '', value: '' }]);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  const updateRow = (i, field, val) => setResultRows(resultRows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const addRow = () => setResultRows([...resultRows, { key: '', value: '' }]);

  const save = async () => {
    if (!materialName.trim()) return toast.error('Material name is required');
    const results = {};
    resultRows.forEach((r) => { if (r.key.trim()) results[r.key.trim()] = r.value; });
    setSaving(true);
    try {
      const t = await apiPost('/lab/material-tests', { test_type: testType, material_name: materialName.trim(), results, remarks });
      toast.success(`${t.test_no} logged`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log test'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title="Log Material Test" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Test Type">
            <select value={testType} onChange={(e) => setTestType(e.target.value)} className="input">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Material"><input value={materialName} onChange={(e) => setMaterialName(e.target.value)} className="input" placeholder="20mm Aggregate" /></Field>
        </div>
        <div>
          <label className="label">Results</label>
          <div className="space-y-2">
            {resultRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={row.key} onChange={(e) => updateRow(i, 'key', e.target.value)} className="input flex-1" placeholder="e.g. Flakiness Index %" />
                <input value={row.value} onChange={(e) => updateRow(i, 'value', e.target.value)} className="input w-32" placeholder="Value" />
              </div>
            ))}
          </div>
          <button onClick={addRow} className="mt-2 text-xs font-semibold text-[#1F7A6E] flex items-center gap-1"><Plus size={12} /> Add field</button>
        </div>
        <Field label="Remarks (optional)"><textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input" rows={2} /></Field>
        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center mt-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save Test'}
        </button>
      </div>
    </ModalShell>
  );
}
